import { Link } from "react-router-dom";
import { BadgeCheck, ArrowRight } from "lucide-react";

/**
 * Coming-soon panel for the CAB ID issuance/claim program, shown wherever the
 * program is gated off by CAB_ID_ENABLED (dashboard claim flow, claim-verify,
 * admin claims queue, broker CA signup path). Canadian broker lookup /
 * verification stays live, so the panel points the user there.
 *
 * Styled with dash-v2 tokens so it reads correctly both inside the dashboard
 * shell and on the broker signup page (both use the dash-bg surface).
 */
interface CabIdComingSoonProps {
  /** Where the "verify a broker" action points (lookup is still live). */
  verifyHref?: string;
  verifyLabel?: string;
}

export function CabIdComingSoon({
  verifyHref = "/verify",
  verifyLabel = "Verify a Canadian broker",
}: CabIdComingSoonProps) {
  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-[480px] rounded-2xl border border-dash-border bg-dash-elevated p-8 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-dash-mint/30 bg-dash-mint/10 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.16em] text-dash-mint">
          Coming soon
        </span>
        <div className="mx-auto mt-5 flex h-12 w-12 items-center justify-center rounded-xl bg-dash-mint/10">
          <BadgeCheck className="h-6 w-6 text-dash-mint" aria-hidden />
        </div>
        <h1 className="mt-4 text-[20px] font-semibold text-dash-text">CAB ID is coming soon</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-dash-secondary">
          Cipher &amp; Row's independent Canadian broker ID program is launching soon. You'll be able to claim a
          verified CAB-&lt;PROVINCE&gt;-XXXXXXX identifier for your brokerage right here.
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-dash-secondary">
          In the meantime, you can still look up and verify any Canadian broker.
        </p>
        <Link
          to={verifyHref}
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-dash-border px-4 py-2.5 text-[13px] font-semibold text-dash-text transition hover:border-dash-mint/40 hover:bg-dash-mint/5"
        >
          {verifyLabel} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

export default CabIdComingSoon;
